import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLoad } from "@/lib/hooks";
import { fdatetime } from "@/lib/api";
import { cn } from "@/lib/utils";
import { PageHeader, Section, EmptyRow } from "@/components/common";
import { ImportWizard } from "@/components/import/ImportWizard";

function RecentImports({ runs, labels }) {
  return (
    <Section title="Recent imports" testId="recent-imports">
      <div className="overflow-x-auto"><table className="po-table">
        <thead><tr><th>When</th><th>Type</th><th>File</th><th className="po-num">Imported</th><th className="po-num">Skipped</th><th>By</th></tr></thead>
        <tbody>{runs?.length ? runs.map((r) => (
          <tr key={r.id} data-testid={`import-run-${r.id}`}><td>{fdatetime(r.at)}</td><td>{labels[r.type] || r.type}{r.qb_step && " (QuickBooks)"}</td><td>{r.filename}</td>
            <td className="po-num">{r.imported}</td><td className="po-num">{r.skipped}</td><td>{r.created_by}</td></tr>)) : <EmptyRow cols={6} text="No imports yet." />}</tbody>
      </table></div>
    </Section>
  );
}

function FieldGuide({ spec }) {
  if (!spec) return null;
  const req = spec.fields.filter((f) => f.required), opt = spec.fields.filter((f) => !f.required);
  const chip = (f, on) => <span key={f.key} title={f.aliases?.length ? `Also recognised: ${f.aliases.join(", ")}` : undefined} className={cn("rounded px-2 py-0.5 text-xs", on ? "bg-[#13294B] text-white" : "bg-slate-100 text-slate-700")}>{f.label}</span>;
  return (
    <div className="grid gap-3 border-b border-slate-200 px-5 py-4 sm:grid-cols-2" data-testid="import-field-guide">
      <div><div className="po-label mb-1.5">Required columns</div><div className="flex flex-wrap gap-1.5" data-testid="import-required-fields">{req.map((f) => chip(f, true))}</div></div>
      <div><div className="po-label mb-1.5">Optional columns</div><div className="flex flex-wrap gap-1.5" data-testid="import-optional-fields">{opt.length ? opt.map((f) => chip(f, false)) : <span className="text-xs text-slate-400">None</span>}</div></div>
      <p className="text-xs text-slate-500 sm:col-span-2">Column names don't need to match exactly. You can map them on the next step. Hover a column to see other names we recognise.</p>
    </div>);
}

export default function DataImport() {
  const [types] = useLoad("/imports/types");
  const [runs, reloadRuns] = useLoad("/imports/runs");
  const [type, setType] = useState("customers");
  if (!types) return null;
  const general = types.filter((t) => !t.qb_only);
  const labels = Object.fromEntries(types.map((t) => [t.type, t.label]));
  return (
    <div className="space-y-6">
      <PageHeader title="Data import" subtitle="Bring customers, suppliers, products, stock, accounts and opening balances in from Excel or CSV.">
        <Button asChild variant="outline" data-testid="qb-migration-link"><Link to="/app/settings/import/quickbooks">QuickBooks migration<ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
      </PageHeader>
      <Section title="What are you importing?" testId="import-type-section">
        <div className="flex flex-wrap gap-2 border-b border-slate-200 px-5 py-4">
          {general.map((t) => (
            <button key={t.type} data-testid={`import-type-${t.type}`} onClick={() => setType(t.type)}
              className={cn("rounded-full border px-3.5 py-1.5 text-sm transition-colors", type === t.type ? "border-[#13294B] bg-[#13294B] text-white" : "border-slate-200 text-slate-700 hover:border-slate-400")}>{t.label}</button>))}
        </div>
        <FieldGuide spec={types.find((t) => t.type === type)} />
        <ImportWizard key={type} type={type} onDone={reloadRuns} />
      </Section>
      <RecentImports runs={runs} labels={labels} />
    </div>
  );
}
